import { AlertCircle } from "lucide-react";
import {
  Alert,
  Button,
  Container,
  Drawer,
  Radio,
  RadioGroup,
  Space,
  Text,
  Textarea,
} from "@mantine/core";
import React, { useCallback, useEffect, useState } from "react";
import useEmployeeBulkStatusApiService from "../services/api/EmployeeBulkStatusApiService";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";

const EmployeeBulkStatusUpdate: React.FC = () => {
  const flagEmployeeBulkStatusUpdate = useEnabledFeatureFlag(
    FeatureFlags.EMPLOYEE_BULK_STATUS_UPDATE
  );

  const [opened, setOpened] = useState(false);
  const [status, setStatus] = useState<"active" | "suspended" | "deleted">(
    "active"
  );
  const [employees, setEmployees] = useState<string>("");

  const { data, isLoading, isSuccess, isError, mutate } =
    useEmployeeBulkStatusApiService();

  const downloadReport = useCallback((): void => {
    const element = document.createElement("a");

    const csv = [`Employees, Status, Updated, Reason`];

    data?.updated_employees?.forEach((employee) => {
      csv.push(`${employee},${status},true,Updated successfully`);
    });

    data?.failed_employees?.forEach((employee) => {
      csv.push(`${employee},N/A,false,Could not find employee`);
    });

    data?.already_updated?.forEach((employee) => {
      csv.push(`${employee},${status},false,Already in the requested status`);
    });

    const csvString = csv.join("\n");
    element.href = URL.createObjectURL(
      new Blob([csvString], { type: "text/csv" })
    );
    element.download = "employee-status-update-report.csv";
    document.body.appendChild(element);
    element.click();

    element.remove();
    setEmployees("");
    setStatus("active");
    window.location.reload();
  }, [data]);

  useEffect(() => {
    if (isSuccess) {
      setEmployees("");
      setStatus("active");
    }
  }, [isSuccess]);

  if (!flagEmployeeBulkStatusUpdate) {
    return null;
  }

  return (
    <>
      <span className="hidden sm:block ml-3">
        <Button
          variant="filled"
          type="button"
          color="jem-theme"
          onClick={() => setOpened(true)}
        >
          Bulk Update Status
        </Button>
      </span>
      <Drawer
        opened={opened}
        size="xl"
        onClose={() => setOpened(false)}
        title="Bulk Employee Status Update"
        overlayOpacity={0.1}
        overlayBlur={3}
        position="right"
      >
        <Container className="px-3 py-4">
          {isError && (
            <Alert icon={<AlertCircle size={16} />} color="yellow" radius="md">
              <span className="text-yellow-600 font-bold">Oops! </span>
              Something went wrong!
            </Alert>
          )}
          {isSuccess && (
            <>
              <Alert icon={<AlertCircle size={16} />} color="green" radius="md">
                <span className="text-green-600 font-bold">Success! </span>
                Employee status updated successfully!
              </Alert>
              <Space h="lg" />
            </>
          )}
          {!isSuccess && (
            <>
              <Textarea
                label="Employee ID Numbers"
                placeholder="Enter employee ID numbers separated by commas"
                required
                style={{ resize: "none" }}
                onChange={(event) => {
                  let { value } = event.currentTarget;
                  // Remove any spaces
                  value = value.replace(/\s/g, "");
                  // Remove any duplicate commas
                  value = value.replace(/,{2,}/g, ",");
                  // Remove any commas at the start
                  value = value.replace(/^,/, "");
                  setEmployees(value);
                }}
                value={employees}
              />
              {employees && (
                <Text>
                  {employees.split(",").filter((e) => e).length} Employees
                </Text>
              )}
              <Space h="md" />
              <RadioGroup
                orientation="vertical"
                label="Select status"
                required
                onChange={(s) =>
                  ["active", "suspended", "deleted"].includes(s) &&
                  setStatus(s as "active" | "suspended" | "deleted")
                }
                value={status}
              >
                <Radio value="active" label="Activate" />
                <Radio value="suspended" label="Suspend" />
                <Radio value="deleted" label="Delete" />
              </RadioGroup>
            </>
          )}

          <Space h="md" />
          <Button
            type="submit"
            variant="filled"
            fullWidth
            loading={isLoading}
            loaderPosition="right"
            className="float-right"
            color="jem-theme"
            onClick={() =>
              isSuccess
                ? downloadReport()
                : mutate({ employee_id_numbers: employees, status })
            }
          >
            {isSuccess ? "Download Report" : "Update"}
          </Button>
        </Container>
      </Drawer>
    </>
  );
};

export default EmployeeBulkStatusUpdate;
